import { Alert, Modal, Form, DatePicker, Select, Upload, Button, App as AntdApp } from "antd";
import { InfoCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { useApiUrl } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { tokenService } from "../../../services/storage/tokenService";
import axios from "axios";
import { useAssignableAuditors } from "./useAssignableAuditors";

interface RouteUploadModalProps {
  visible: boolean;
  onClose: () => void;
  planningId: number;
  auditors: Array<{ id: number; name: string }>;
  auditCompanyId?: number | null;
  countryId?: number | null;
  isAuditorOnly?: boolean;
  currentUser?: { id: number; name: string } | null;
  onSuccess?: () => void;
  /** Lista de rutas ya cargadas en el planning. La usamos para
   *  avisar al usuario cuando la fecha + auditor seleccionados ya
   *  tienen una ruta existente — en ese caso el POST se trata como
   *  "añadir N ficheros a la ruta existente", no como "crear nueva". */
  existingRoutes?: Array<{
    id: number;
    route_date: string;
    auditor: number;
    sources_count?: number;
  }>;
}

interface RouteFormValues {
  route_date: dayjs.Dayjs;
  auditor: number;
  gpx_file: UploadFile[];
}

/**
 * RouteUploadModal Component
 * Modal for uploading GPS route files (GPX or KML) for auditors.
 * El backend distingue GPX y KML por extensión y extrae el track
 * con el parser adecuado — el frontend solo necesita aceptar
 * ambas extensiones y enviar el nombre original.
 */
const ALLOWED_EXTS = [".gpx", ".kml"];
const ACCEPT_ATTR = ALLOWED_EXTS.join(",");

const hasAllowedExt = (name: string) =>
  ALLOWED_EXTS.some((ext) => name.toLowerCase().endsWith(ext));
export const RouteUploadModal = ({
  visible,
  onClose,
  planningId,
  auditors,
  auditCompanyId,
  countryId,
  isAuditorOnly = false,
  currentUser,
  onSuccess,
  existingRoutes = [],
}: RouteUploadModalProps) => {
  const { t } = useTranslation();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm<RouteFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  // Antd v5 deprecó el `message` estático: usar el del contexto
  // <App> para que los toasts se rendericen dentro del provider de
  // theme/locale. Sin esto los success/error se "tragan" silenciosamente
  // en componentes que renderizan vía portal (como este Modal).
  const { message: notify } = AntdApp.useApp();

  const auditorPicker = useAssignableAuditors({
    visible,
    auditCompanyId,
    countryId,
    isAuditorOnly,
    currentUser,
    fallback: auditors,
  });

  // When the picker is locked to the current user (type=5), preselect
  // them so the form is one click shorter.
  useEffect(() => {
    if (visible && auditorPicker.locked && auditorPicker.defaultValue != null) {
      form.setFieldsValue({ auditor: auditorPicker.defaultValue });
    }
  }, [visible, auditorPicker.locked, auditorPicker.defaultValue, form]);

  const handleSubmit = async (values: RouteFormValues) => {
    if (fileList.length === 0) {
      notify.error(
        t("route.route_file_required") ||
          "Debes seleccionar al menos un archivo GPX o KML",
      );
      return;
    }
    const originals = fileList
      .map((f) => f.originFileObj as File | undefined)
      .filter((f): f is File => f != null);
    if (originals.length === 0) {
      notify.error("No se pudo obtener los archivos");
      return;
    }

    setLoading(true);
    try {
      // Multipart: enviar `files` como lista de uploads reales. El
      // backend acepta N ficheros en un único POST y los mergea.
      // Dropeamos el base64 porque es frágil (CPU + memoria O(n) por
      // fichero × transporte, y hemos visto GPX de 20+ MB) y no
      // encaja con multi-upload sin encoding adicional.
      const formData = new FormData();
      formData.append("route_date", values.route_date.format("YYYY-MM-DD"));
      formData.append("auditor", String(values.auditor));
      formData.append("planning", String(planningId));
      for (const f of originals) {
        formData.append("files", f, f.name);
      }

      const accessToken = tokenService.getAccessToken();
      await axios.post(`${apiUrl}/routes/`, formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      notify.success(
        originals.length === 1
          ? t("route.upload_success_single") ||
              "Archivo subido correctamente"
          : t("route.upload_success_many", {
              count: originals.length,
              defaultValue: `{{count}} archivos subidos correctamente`,
            }) || `${originals.length} archivos subidos correctamente`,
      );
      form.resetFields();
      setFileList([]);
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      const errorMessage =
        error?.response?.data?.files?.[0] ||
        error?.response?.data?.gpx_file?.[0] ||
        error?.response?.data?.route_date?.[0] ||
        error?.response?.data?.auditor?.[0] ||
        error?.response?.data?.non_field_errors?.[0] ||
        error?.response?.data?.detail ||
        error?.message ||
        t("route.upload_error") ||
        "Error al subir la ruta";
      notify.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  // Custom upload validation
  const beforeUpload = (file: File) => {
    if (!hasAllowedExt(file.name)) {
      notify.error(
        t("route.invalid_route_file") ||
          "El archivo debe ser GPX (.gpx) o KML (.kml)",
      );
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  const handleFileChange = (info: any) => {
    // Multi-upload: aceptamos hasta 10 ficheros (sanity cap para no
    // reventar peticiones si el user arrastra una carpeta entera por
    // error). Antes forzábamos `.slice(-1)` que descartaba todo
    // excepto el último.
    setFileList(info.fileList.slice(0, 10));
  };

  // Watch del formulario para detectar en vivo si (route_date,
  // auditor) corresponde a una ruta ya existente — así podemos pintar
  // el badge "añadir a N archivos existentes" en lugar de "nueva".
  const watchedDate = Form.useWatch("route_date", form);
  const watchedAuditor = Form.useWatch("auditor", form);
  const existingMatch = (() => {
    if (!watchedDate || !watchedAuditor) return null;
    const iso = watchedDate.format("YYYY-MM-DD");
    return (
      existingRoutes.find(
        (r) => r.route_date === iso && r.auditor === watchedAuditor,
      ) ?? null
    );
  })();

  return (
    <Modal
      title={t("route.upload_route") || "Subir Ruta GPS"}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t("common.save") || "Guardar"}
      cancelText={t("common.cancel") || "Cancelar"}
      confirmLoading={loading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          name="route_date"
          label={t("route.route_date") || "Fecha de Ruta"}
          rules={[
            {
              required: true,
              message: `${t("route.route_date") || "Fecha de Ruta"} ${t("common.required") || "es requerido"}`,
            },
          ]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={`${t("common.select")} ${t("route.route_date") || "fecha de ruta"}`}
          />
        </Form.Item>

        <Form.Item
          name="auditor"
          label={t("route.auditor") || "Auditor"}
          rules={[
            {
              required: true,
              message: `${t("route.auditor") || "Auditor"} ${t("common.required") || "es requerido"}`,
            },
          ]}
        >
          <Select
            placeholder={`${t("common.select")} ${t("route.auditor") || "auditor"}`}
            showSearch
            optionFilterProp="label"
            loading={auditorPicker.loading}
            disabled={auditorPicker.locked}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            options={auditorPicker.options}
          />
        </Form.Item>

        {existingMatch && (
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 12 }}
            message={
              t("route.append_notice", {
                defaultValue:
                  "Ya hay una ruta para este auditor y fecha ({{count}} archivos). " +
                  "Los nuevos ficheros se AÑADIRÁN al recorrido existente.",
                count: existingMatch.sources_count ?? 0,
              })
            }
            description={
              <span style={{ fontSize: 12 }}>
                {t(
                  "route.append_hint",
                  "Para empezar de cero, abre el recorrido en 'Ver ruta' y usa 'Resetear'.",
                )}
              </span>
            }
          />
        )}

        <Form.Item
          name="gpx_file"
          label={
            t("route.route_files_label") ||
            "Archivos de ruta (GPX o KML, puedes añadir varios)"
          }
          rules={[
            {
              validator: () =>
                fileList.length > 0
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        t("route.route_file_required") ||
                          "Debes seleccionar al menos un archivo GPX o KML",
                      ),
                    ),
            },
          ]}
        >
          <Upload
            fileList={fileList}
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            multiple
            maxCount={10}
            accept={ACCEPT_ATTR}
          >
            <Button icon={<UploadOutlined />}>
              {t("route.select_route_files") ||
                "Seleccionar archivos (.gpx / .kml)"}
            </Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};
